import React from 'react';
import Select, { StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useTheme, alpha, Box, Typography } from '@mui/material';
import { SelectOptions } from '../../../../utils/types/react_select_types';

interface SelectProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  isMulti?: boolean;
  value?: SelectOptions | SelectOptions[];
  onChange: (value: any) => void;
  loadOptions?: (
    inputValue: string,
    callback: (options: SelectOptions[]) => void
  ) => void;
  options?: SelectOptions[];
  defaultOptions?: boolean | SelectOptions[];
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  isDisabled?: boolean;
}

export const FloatingLabelSelect: React.FC<SelectProps> = ({
  label,
  placeholder = 'Search...',
  required = false,
  isMulti = false,
  value,
  onChange,
  loadOptions,
  options,
  defaultOptions,
  error = false,
  helperText = '',
  onBlur,
  isDisabled = false,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main || '#0284c7';
  const isAsync = typeof loadOptions === 'function';

  const customStyles: StylesConfig<SelectOptions, boolean> = {
    control: (base, state) => ({
      ...base,
      minHeight: '36px',
      height: '36px',
      backgroundColor: state.isDisabled ? '#f8fafc' : '#fff',
      borderColor: error ? '#f87171' : state.isFocused ? primaryColor : '#e2e8f0',
      borderWidth: '1px',
      boxShadow: state.isFocused ? `0 0 0 3px ${alpha(primaryColor, 0.15)}` : 'none',
      '&:hover': {
        borderColor: state.isFocused ? primaryColor : '#cbd5e1',
      },
      zIndex: 50,
      borderRadius: '0.625rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px',
      fontSize: '13px',
      fontWeight: '500',
      color: '#475569',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      textAlign: 'left',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '4px 8px',
      color: '#94a3b8',
      '&:hover': {
        color: '#64748b',
      },
      svg: {
        width: '16px',
        height: '16px',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '4px',
      svg: {
        width: '16px',
        height: '16px',
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '0.75rem',
      boxShadow:
        '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
      overflow: 'hidden',
      marginTop: '6px',
      border: '1px solid #e2e8f0',
    }),
    singleValue: (base) => ({
      ...base,
      textAlign: 'left',
      color: '#475569',
    }),
    placeholder: (base) => ({
      ...base,
      textAlign: 'left',
      color: '#94a3b8',
      fontSize: '13px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? alpha(primaryColor, 0.1)
        : state.isFocused
          ? alpha(primaryColor, 0.04)
          : 'transparent',
      color: state.isSelected ? primaryColor : '#475569',
      fontWeight: state.isSelected ? '600' : '500',
      cursor: 'pointer',
      fontSize: '13px',
      padding: '8px 12px',
      textAlign: 'left',
      ':active': {
        backgroundColor: alpha(primaryColor, 0.15),
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: alpha(primaryColor, 0.08),
      borderRadius: '9999px',
      fontSize: '11px',
      border: `1px solid ${alpha(primaryColor, 0.2)}`,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: primaryColor,
      padding: '1px 8px',
      fontWeight: '600',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: primaryColor,
      borderRadius: '9999px',
      ':hover': {
        backgroundColor: primaryColor,
        color: '#ffffff',
      },
    }),
  };

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            mb: 0.5,
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            ml: 0.25,
          }}
        >
          {label} {required && <Box component="span" sx={{ color: 'error.main' }}>*</Box>}
        </Typography>
      )}

      {isAsync ? (
        <AsyncSelect
          cacheOptions
          defaultOptions={defaultOptions}
          isMulti={isMulti}
          value={value}
          onChange={onChange}
          loadOptions={loadOptions}
          placeholder={placeholder}
          onBlur={onBlur}
          styles={customStyles}
          menuPortalTarget={document.body}
          isDisabled={isDisabled}
        />
      ) : (
        <Select
          options={options}
          isMulti={isMulti}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onBlur={onBlur}
          styles={customStyles}
          menuPortalTarget={document.body}
          isDisabled={isDisabled}
        />
      )}

      {error && helperText && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: '10px',
            color: 'error.main',
            fontWeight: 500,
            ml: 0.25,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default FloatingLabelSelect;

